import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import { FeedbackRating } from './subsections/FeedbackRating';
import {
  isFeedbackPersistedProps,
  type FeedbackProps,
  type FeedbackPersistedProps,
  type FeedbackInitialProps,
} from './useFeedback';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { spacing } from '../../../theme';

const FeedbackInitialLayout: React.FC<FeedbackInitialProps> = ({
  formData,
  updateFormData,
  disabled = false,
  requestId = null,
  isOnline = true,
}) => (
  <View style={styles.container}>
    <Text style={styles.pageTitle}>Feedback</Text>

    <FeedbackRating
      mode="initial"
      formData={formData}
      updateFormData={updateFormData}
      disabled={disabled}
      requestId={requestId}
      isOnline={isOnline}
    />
  </View>
);

const FeedbackPersistedLayout: React.FC<FeedbackPersistedProps> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  isOnline = true,
}) => (
  <View style={styles.container}>
    <PersistedStepHeader
      title="Feedback"
      uomSlot={null}
      actionsSlot={
        showViewToggle && onToggleView ? (
          <TouchableOpacity
            style={styles.viewToggleButton}
            onPress={onToggleView}
          >
            {isAdvancedView ? (
              <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
            ) : (
              <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
            )}
            <Text style={styles.viewToggleText}>
              {isAdvancedView ? 'Advanced' : 'Basic'}
            </Text>
          </TouchableOpacity>
        ) : null
      }
    />

    <FeedbackRating
      mode="persisted"
      request={request}
      isEditing={isEditing}
      editedData={editedData}
      updateEditedData={updateEditedData}
      showFullDetails={showFullDetails}
      isOnline={isOnline}
    />
  </View>
);

export const Feedback: React.FC<FeedbackProps> = props =>
  isFeedbackPersistedProps(props) ? (
    <FeedbackPersistedLayout {...props} />
  ) : (
    <FeedbackInitialLayout {...props} />
  );

Feedback.displayName = 'Feedback';

/** Caption / legacy tests — compose path only. */
export const FeedbackStep = FeedbackInitialLayout;

/** @deprecated Test / caption suites — persisted request detail branch */
export const FeedbackInRequest = FeedbackPersistedLayout;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {},
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
});
